// AssetActions — the asset's ONE registry and its overlays (ADR-036 §7 — the
// four kinds; mirrors the exemplar `pages/teams/TeamActions.tsx`).
//
//   • `assetActs` is the PURE registry — rows with a kind, a section, a gate and
//     a `show` predicate over the closed-over facts; the shape test
//     (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useAssetActions` resolves it for an asset and returns the ACTS every
//     surface composes from — the peek's top row through `ContextPanel`, the
//     page's seat + door through `EntityHeader`, the Books card's link through
//     `SectionAct`, the Connectivity tab's seat through `seatOfAct` — and the
//     OVERLAYS (every drawer, mounted ONCE by the caller, each on open: a form
//     seeds from a fresh read, so a peek→peek switch never saves the wrong row).
//
// The asset's edits are FACET drawers, each with its home on the page: Edit
// asset the primary (the record drawer — its fields span several cards: the
// seat, the peek's main button); Edit books the Books card's link (the drawer
// edits what the card shows); Edit connectivity the Connectivity TAB's seat
// while that tab is open (section `connectivity` — the tab is its place); Device
// settings section-less and DISABLED (advertised — the remote-config seam has
// not landed; exception 2: the page's door, the peek's Edit menu). The peek folds
// the three into its Edit menu. Rows are structurally absent where they can
// never apply: books only on a category that keeps books (and only with the
// books read loaded — the drawer seeds from it); the device rows only on a
// network-capable asset. The asset's lifecycle joins the door when the asset
// wave lands it.

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isBooksCategory } from '@ems-app/schemas/assets/categories';
import type { AssetBooks, AssetDetail } from '@ems-app/schemas/assets/responses';
import type { EditAssetInput } from '@ems-app/schemas/assets/verbs';
import { apiClient } from '../../lib/apiClient.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { assetsKeys, fetchAssetAccess, useAssetAccess } from '../../hooks/useAssets.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { useToast } from '../../components/ui/Toast.js';
import { icons } from '../../components/ui/icons.js';
import { AssetBooksDrawer, AssetEditDrawer } from './AssetEditDrawers.js';
import { AssetConnectivityDrawer, viaSourceOf, type ConnectivityFacts, type ViaSource } from './AssetConnectivityDrawer.js';
import { useLookup } from '../../hooks/useLookup.js';

const EMPTY: readonly string[] = [];

/** One status today — the asset's lifecycle verbs (and their status predicates) land with the asset wave. */
export type AssetActStatus = 'any';

/** What each act does — the hook wires these to its overlays. */
export interface AssetActHandlers {
  editAsset: () => void;
  editBooks: () => void;
  editConnectivity: () => void;
}

/** The closed-over facts the rows read (the category's and the read's). */
export interface AssetActFacts {
  /** The category keeps books AND the books read is loaded (the drawer seeds from it). */
  books: boolean;
  /** The category is network-capable — the device facets exist. */
  networkCapable: boolean;
}

/** The asset's registry — pure: the rows, their kinds, sections and predicates. */
export function assetActs(h: AssetActHandlers, facts: AssetActFacts): ActionItem<AssetActStatus>[] {
  return [
    // The primary — the record drawer (its fields span several cards): the seat, the peek's main button.
    { id: 'edit-asset', kind: 'primary', labelKey: 'edit.asset', icon: icons.edit, run: { handler: h.editAsset }, gate: 'asset:write', order: 10 },
    // The Books card's own act.
    { id: 'edit-books', kind: 'edit', section: 'books', labelKey: 'edit.books', icon: icons.books, run: { handler: h.editBooks }, gate: 'asset.books:write', order: 10, show: () => facts.books },
    // The Connectivity tab's act — its seat while the tab is open (the tab is its place).
    { id: 'edit-connectivity', kind: 'edit', section: 'connectivity', labelKey: 'device.drawer.title', icon: icons.devices, run: { handler: h.editConnectivity }, gate: 'asset:write', order: 20, show: () => facts.networkCapable },
    // Advertised, unwired — the remote-config seam has not landed (a designed disabled state, no drawer).
    { id: 'edit-settings', kind: 'edit', labelKey: 'edit.settings', icon: icons.settings, run: { handler: () => {} }, gate: 'asset:write', order: 30, disabled: true, show: () => facts.networkCapable },
  ];
}

export interface AssetActions {
  /** The resolved set — what the shells, the Books card and the Connectivity tab compose from. */
  acts: EntityAct[];
  /** Every drawer, mounted ONCE by the caller beside its surface. */
  overlays: ReactNode;
}

/** `asset` may still be loading (the peek keeps ONE panel across the load): no acts and no overlays until it is there. */
export function useAssetActions(projectId: string, asset: AssetDetail | undefined, books: AssetBooks | undefined): AssetActions {
  const { t } = useTranslation('assets');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  // The open drawer, held as the facet AND the asset it was opened for — the hook outlives an asset switch (the peek
  // keeps one panel across the load), so a drawer open on one asset never reopens on the next.
  const [open, setOpen] = useState<{ facet: 'record' | 'books' | 'connectivity'; assetId: string } | null>(null);

  if (!asset) return { acts: [], overlays: null };
  const opened = (facet: 'record' | 'books' | 'connectivity') => open?.facet === facet && open.assetId === asset.id;
  const close = () => setOpen(null);
  const acts = resolveActs(
    assetActs(
      {
        editAsset: () => setOpen({ facet: 'record', assetId: asset.id }),
        editBooks: () => setOpen({ facet: 'books', assetId: asset.id }),
        editConnectivity: () => setOpen({ facet: 'connectivity', assetId: asset.id }),
      },
      { books: books !== undefined && isBooksCategory(asset.category), networkCapable: asset.network_capable },
    ),
    { can, entitlements, status: 'any' },
    (key) => t(key),
  );

  // mount-on-open (the SitePeek recipe): each open seeds fresh from the loaded reads.
  const overlays = (
    <>
      {opened('record') && <AssetEditDrawer projectId={projectId} asset={asset} open onClose={close} />}
      {opened('books') && books && (
        <AssetBooksDrawer projectId={projectId} assetId={asset.id} category={asset.category} reference={asset.reference} books={books} open onClose={close} />
      )}
      {opened('connectivity') && <AssetConnectivityDoor projectId={projectId} asset={asset} onClose={close} />}
    </>
  );

  return { acts, overlays };
}

// ── the Connectivity door — loads the facet + the Via population, then mounts
// the drawer (mount-on-open: the form seeds from a FRESH read every time) ────

/** The via question of a unit standing AT a site — none for one in a site's storage (the location's site is then the
 *  storage's host), a depot or a vehicle: only a unit standing at a site reaches the network through another there. */
export function connectivityViaSource(asset: Pick<AssetDetail, 'id' | 'location'>): ViaSource | null {
  return viaSourceOf(asset.location?.kind === 'site' ? asset.location.site_id : null, asset.id);
}

function AssetConnectivityDoor({
  projectId,
  asset,
  onClose,
}: {
  projectId: string;
  asset: AssetDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation('assets');
  const toast = useToast();
  const queryClient = useQueryClient();

  const accessQ = useAssetAccess(projectId, asset.id);
  // "Reached through" is the unit lookup inside this site (ADR-078 §6 — the
  // rule is the server's); the START answer is read here so the drawer seeds
  // its pre-fill from the site's one unit built to carry others.
  const viaSource = useMemo(() => connectivityViaSource(asset), [asset]);
  const viaStart = useLookup(projectId, viaSource ?? { entity: 'assets', params: {} }, EMPTY, viaSource !== null);
  const likely = useMemo(() => viaStart.rows.filter((r) => r.group !== null), [viaStart.rows]);

  // The facts whole, with the stamp the facet read carried as the drawer opened (ADR-084 §3).
  const submit = async (facts: ConnectivityFacts, stamp: number | undefined) => {
    const body: EditAssetInput['facts'] = {
      deviceIp: facts.deviceIp,
      accessedViaAssetId: facts.accessedViaAssetId,
      deviceAccess: facts.deviceAccess,
    };
    await apiClient.patch(`/projects/${projectId}/assets/${asset.id}`, { row_version: stamp, facts: body });
    await queryClient.invalidateQueries({ queryKey: assetsKeys.all(projectId) });
    toast.success(t('edit.saved'));
  };

  // The drawer seeds from the access read — a brief loader beats a form that
  // re-seeds under the user's hands.
  if (accessQ.isPending || !viaStart.settled) {
    return (
      <div className="fixed inset-0 z-overlay grid place-items-center bg-[var(--color-overlay)]">
        <PageLoader />
      </div>
    );
  }
  if (!accessQ.data) return null;

  return (
    <AssetConnectivityDrawer
      access={accessQ.data}
      assetCategory={asset.category}
      projectId={projectId}
      viaSource={viaSource}
      likely={likely}
      open
      onClose={onClose}
      // The fresh facet: a quiet read (a unit gone meanwhile is said in the form), into the door's entry once it answers.
      reload={() => readLatest(queryClient, assetsKeys.access(projectId, asset.id), () => fetchAssetAccess(projectId, asset.id, true))}
      onSubmit={submit}
    />
  );
}
